//LIBRARIES
import React from 'react';
import { connect } from 'react-redux';
import { Field, change, FieldArray, formValueSelector } from 'redux-form';
import { TextField, DatePicker } from 'redux-form-material-ui';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import isEqual from 'lodash/isEqual';
import { CSSTransitionGroup } from 'react-transition-group';

//HELPERS
import { fetchCurrentLeasesForSingleUnits } from 'helpers/PropertiesHelper';
import { BooleanRadioButtons } from 'helpers/BooleanRadioButtons';
import * as formatter from 'helpers/Formatter';
import * as validator from 'helpers/Validator';
import { getTranslatedString } from 'helpers/i18n';
import { featureEnabled } from 'helpers/ToggleBot';

//COMPONENTS
import Header from 'components/shared/Header';
import CurrentLeasesOfUnit from 'components/shared/CurrentLeasesOfUnit';
import PropertyUnitSelector from 'components/shared/PropertyUnitSelector';
import LeaseItems from 'components/shared/LeaseItems/LeaseItems';
import RentalIncentiveFields from 'components/landlord_app/applications/components/common/RentalIncentiveFields';
import TenantResidentVerbiageHelper from 'components/shared/TenantResidentVerbiageHelper';

//ACTIONS
import { getProperty, getUnitTypes } from 'actions/landlordPropertiesActions';
import { getLandlordTrafficSources } from 'actions/landlordLeadsActions';

const yardiStyle = {
  textAlign: 'left',
  color: '#6f6f6f',
  lineHeight: '15px',
  fontSize: '12px',
};

let LeaseTermsForm = class extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showIncentiveFields: false,
      leaseStartDate: null,
      leaseEndDate: null,
      propertySelected: false,
      selectedUnitTypeDeposit: 0,
      unitSelected: null,
      monthlyRentAmount: 1,
    };
  }

  toggleIncentiveFields = () => {
    this.setState((prevState) => ({
      showIncentiveFields: !prevState.showIncentiveFields,
    }));
  };

  componentDidUpdate = (prevProps) => {
    if (this.state.unitSelected === false) return;

    if (
      !this.props.unitMarketRent &&
      !this.props.unitAbsoluteRent &&
      !this.props.unitMarketRentSixMonths &&
      !this.props.unitDepositAmount
    )
      return;

    const lengthInMonths = formatter.differenceInMonthsRounded(
      this.state.leaseStartDate,
      this.state.leaseEndDate
    );

    if (!isEqual(prevProps, this.props)) {
      this.setMonthlyRentFields(
        lengthInMonths,
        this.props.unitMarketRent,
        this.props.unitAbsoluteRent,
        this.props.unitMarketRentSixMonths,
        this.props.unitDepositAmount
      );
    }
  };

  resetMonthlyRent = (e, value) => {
    this.setState({ leaseEndDate: value });
    if (!this.state.leaseStartDate) return;

    const lengthInMonths = formatter.differenceInMonthsRounded(this.state.leaseStartDate, value);
    this.setMonthlyRentFields(
      lengthInMonths,
      this.props.unitMarketRent,
      this.props.unitAbsoluteRent,
      this.props.unitMarketRentSixMonths,
      this.props.unitDepositAmount
    );
    this.setState({ unitSelected: false });
  };

  setMonthlyRentFields = (
    lengthInMonths,
    unitMarketRent,
    unitAbsoluteRent,
    unitMarketRentSixMonths,
    unitDepositAmount
  ) => {
    let depositAmount = this.props.delayUnitAssignment
      ? this.state.selectedUnitTypeDeposit
      : unitDepositAmount;
    if (depositAmount === '0') {
      depositAmount = null;
    }

    if (lengthInMonths > 11) {
      this.props.changeFieldValue('monthly_rent', unitAbsoluteRent);
      this.props.changeFieldValue('deposit_amount', depositAmount || unitAbsoluteRent);
      this.setState({ monthlyRentAmount: unitAbsoluteRent || 1 });
    }

    if (lengthInMonths === null || lengthInMonths <= 5) {
      this.props.changeFieldValue('monthly_rent', unitMarketRent);
      this.props.changeFieldValue('deposit_amount', depositAmount || unitMarketRent);
      this.setState({ monthlyRentAmount: unitMarketRent || 1 });
    }

    if (lengthInMonths <= 11 && lengthInMonths > 5) {
      this.props.changeFieldValue('monthly_rent', unitMarketRentSixMonths);
      this.props.changeFieldValue('deposit_amount', depositAmount || unitMarketRentSixMonths);
      this.setState({ monthlyRentAmount: unitMarketRentSixMonths || 1 });
    }
  };

  onUnitChange = (event) => {
    if (!event.value || event.value === 'None') return;
    this.setState({ unitSelected: true });
    this.props.getCurrentLeasesOfUnit(event.value);
    this.props.changeFieldValue('unit_type_selected', null);
  };

  onUnitTypeChange = (unitType) => {
    this.setState({ selectedUnitTypeDeposit: unitType.deposit, unitSelected: true });
    this.props.changeFieldValue('deposit_amount', unitType.deposit);
    this.props.changeFieldValue('unit_selected', null);
  };

  doubleUnitBookCheck = (value, fields) => {
    return validator.doubleUnitBook(value, fields, this.props.currentLeasesOfUnit);
  };

  handlePropertyChange = (value) => {
    this.setState({ unitSelected: false, propertySelected: true });
    const propertyId = value && value.value;

    if (propertyId) {
      this.props.getProperty(propertyId);
      this.props.clearCurrentLeasesOfUnit();
      this.props.changeFieldValue('unit_selected', null);
      this.props.changeFieldValue('deposit_amount', '');
      this.props.changeFieldValue('monthly_rent', '');
      this.props.getLandlordTrafficSources(propertyId);
      // Only show the only_rentables if the landlord is yardi enabled.
      this.props.getUnitTypes(propertyId);
      this.props
        .getUnits(propertyId, { for_multiselect: true, only_rentables: this.props.yardiEnabled })
        .then((response) => {
          fetchCurrentLeasesForSingleUnits(
            response.value.data,
            this.props.getCurrentLeasesOfUnit,
            this.props.changeFieldValue,
            this.props.getUnitDetails
          );
        });
      this.setState({ monthlyRentAmount: 1 });
    }
  };

  getFilteredProperties = () => {
    if (!this.props.transferApplication) {
      return this.props.properties;
    }
    if (this.props.onlyYardiBasedProperties) {
      return this.props.properties.filter((property) => property.yardi_based);
    } else {
      return this.props.properties.filter((property) => !property.yardi_based);
    }
  };

  render() {
    const props = this.props;
    const disableDepositSelection = featureEnabled('ui.lease_creator.disable_deposit');
    return (
      <div>
        <form onSubmit={props.handleSubmit}>
          <Header name="LEASE DETAILS" />
          <PropertyUnitSelector
            properties={this.getFilteredProperties()}
            units={props.units}
            unitTypes={props.unitTypes}
            onPropertyChange={this.handlePropertyChange}
            onUnitChange={this.onUnitChange}
            onUnitTypeChange={this.onUnitTypeChange}
            propertyDisabled={this.props.yardiEnabled && !!this.props.leadCard}
            propertyInnerDivStyle={{
              width: '100%',
              fontSize: '14px',
              fontWeight: '100 !important',
            }}
            propertyMenuStyle={{ minWidth: '250px', fontSize: '14px' }}
            propertyStyle={{ textAlign: 'left', fontSize: '14px' }}
            unitMenuStyle={{ fontSize: '14px' }}
            unitStyle={{ textAlign: 'left', fontSize: '14px' }}
            propertySelected={this.state.propertySelected}
            abilityToDelayUnitAssignment={props.abilityToDelayUnitAssignment}
            delayUnitAssignment={props.delayUnitAssignment}
          />

          <CurrentLeasesOfUnit leases={props.currentLeasesOfUnit} />

          <div className="row">
            <div className="col-sm-6">
              <Field
                name="lease_start_date"
                component={DatePicker}
                openToYearSelection
                style={{ fontSize: '14px', width: '100%' }}
                formatDate={formatter.reformatDate}
                autoOk
                textFieldStyle={{ fontSize: '14px', width: '100%' }}
                floatingLabelText="Start Lease Date"
                validate={[validator.required, validator.dateLeaseRange, this.doubleUnitBookCheck]}
                onChange={(e, value) =>
                  this.setState({ leaseStartDate: value }, this.resetMonthlyRent())
                }
              />
            </div>
            <div className="col-sm-6">
              <Field
                name="lease_end_date"
                component={DatePicker}
                openToYearSelection
                style={{ fontSize: '14px', width: '100%' }}
                formatDate={formatter.reformatDate}
                autoOk
                textFieldStyle={{ fontSize: '14px', width: '100%' }}
                floatingLabelText="End Lease Date"
                onChange={(e, value) => this.resetMonthlyRent(e, value)}
                validate={[validator.required, validator.futureDate, validator.dateLeaseRange]}
              />
            </div>
          </div>

          <div className="row" style={{ marginTop: '15px' }}>
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <h4> Early Move-in </h4>
              <div style={{ fontSize: '14px' }}>
                Select the <TenantResidentVerbiageHelper />
                &rsquo;s desired move-in date if it is different from the lease start date above.
              </div>
            </div>
            <div className="col-sm-6 col-sm-offset-3" style={{ textAlign: 'center' }}>
              <Field
                name="early_move_in_date"
                component={DatePicker}
                openToYearSelection={true}
                style={{ fontSize: '14px', width: '100%' }}
                formatDate={formatter.reformatDate}
                autoOk={true}
                textFieldStyle={{ fontSize: '14px' }}
                floatingLabelText="Move In Date"
                validate={[validator.moveInDate]}
              />
            </div>
          </div>
          <div className="row" style={{ marginTop: '15px' }}>
            <div style={{ textAlign: 'center', marginTop: '10px' }}>
              <div style={{ fontSize: '14px' }}>{getTranslatedString('addProratedRentCharge')}</div>
            </div>
            <div className="col-sm-12" style={{ textAlign: 'center', margin: '15px 0' }}>
              <BooleanRadioButtons
                name="create_prorated_charge"
                style={{ width: '187px', margin: 'auto' }}
              />
              <Field name="create_prorated_charge" component={formatter.RenderError} />
            </div>
          </div>
          {this.props.paymentProvider && (
            <div className="row" style={{ marginTop: '15px' }}>
              <div style={{ textAlign: 'center', marginTop: '10px' }}>
                <div style={{ fontSize: '14px' }}>
                  Would you like to collect the first months rent with the deposit?
                </div>
              </div>
              <div className="col-sm-12" style={{ textAlign: 'center', margin: '15px 0' }}>
                <BooleanRadioButtons
                  name="collect_first_months_with_deposit"
                  style={{ width: '187px', margin: 'auto' }}
                />
                <Field name="collect_first_months_with_deposit" component={formatter.RenderError} />
              </div>
            </div>
          )}
          <div className="row" style={{ marginTop: '15px' }}>
            <div style={{ textAlign: 'center', fontSize: '14px' }}>
              Allow this lead to add co-applicants in their application process
            </div>
            <div className="col-sm-12" style={{ textAlign: 'center', margin: '15px 0' }}>
              <BooleanRadioButtons
                name="coapplicants_in_application"
                style={{ width: '187px', margin: 'auto' }}
              />
              <Field name="coapplicants_in_application" component={formatter.RenderError} />
            </div>
          </div>
          <Header name="LEASE PRICING" />
          <div className="row" style={{ marginTop: '15px' }}>
            <div className="col-sm-4" style={{ textAlign: 'center' }}>
              <Field
                name="monthly_rent"
                type="text"
                component={TextField}
                floatingLabelText="Monthly Rent"
                validate={[validator.required]}
                style={{ marginTop: '-15px', fontSize: '14px', width: '100%' }}
                disabled={this.props.disableLeaseWizardSelections}
                {...formatter.currencyMask}
              />
            </div>
            <div className="col-sm-4" style={{ textAlign: 'center' }}>
              <Field
                name="deposit_amount"
                type="text"
                component={TextField}
                floatingLabelText="Rental Deposit"
                validate={[validator.required]}
                style={{ marginTop: '-15px', fontSize: '14px', width: '100%' }}
                disabled={this.props.disableLeaseWizardSelections}
                helperText="Some important text"
                {...formatter.currencyMask}
              />
              {disableDepositSelection && (
                <div className="row">
                  <p style={yardiStyle}>
                    {getTranslatedString(
                      'landlordPortal.leasePricingWizardPage.rentalDepositYardiDisabled'
                    )}
                  </p>
                </div>
              )}
            </div>
            <div className="col-sm-4" style={{ textAlign: 'center', marginBottom: '20px' }}>
              <Field
                name="key_deposit"
                type="text"
                component={TextField}
                floatingLabelText="Key Deposit"
                style={{ marginTop: '-15px', fontSize: '14px', width: '100%' }}
                disabled={this.props.disableLeaseWizardSelections}
                {...formatter.currencyMask}
              />
            </div>
          </div>
          <LeaseItems formName="add_lead_form" />
          {this._renderRentalIncentive()}
        </form>
      </div>
    );
  }

  onConcessionSelect = (value, index) => {
    const selectedConcession = this.props.currentConcessionsOfUnit.filter(
      (concession) => concession.id === value
    );

    let concessionAmount = selectedConcession[0].amount;
    if (selectedConcession[0].concession_type === 'percentage_amount') {
      // Turn the amount to a percentage by dividing by 100.0
      concessionAmount = (selectedConcession[0].amount / 100.0) * this.state.monthlyRentAmount;
    }

    this.props.changeFieldValue(`rental_incentives[${index}].incentive_amount`, concessionAmount);
    this.props.changeFieldValue(
      `rental_incentives[${index}].disableAmountSelection`,
      !selectedConcession[0].modifiable
    );
    this.props.changeFieldValue(`rental_incentives[${index}].term`, selectedConcession[0].term);
  };

  _renderRentalIncentive = () => {
    let selectedConcessionIds = [];
    if (this.props.selectedConcessions && this.props.selectedConcessions.length > 0) {
      selectedConcessionIds = this.props.selectedConcessions.map(
        (concession) => concession.incentive_id
      );
    }

    if (
      this.props.currentConcessionsOfUnit.length === 0 &&
      featureEnabled('ui.lease_creator.enable_concession_selection')
    ) {
      return (
        <div style={{ textAlign: 'center' }}>
          <h3>Rental Incentives</h3>
          <p>There are currently no incentives available for this property</p>
        </div>
      );
    }

    return (
      <div className="row" style={{ marginTop: '15px', textAlign: 'center' }}>
        <FormControlLabel
          control={
            <Switch
              name="toggle_incentive_fields"
              checked={this.state.showIncentiveFields}
              onChange={this.toggleIncentiveFields}
              color="primary"
            />
          }
          label="Add Rental Incentive"
        />

        <CSSTransitionGroup
          transitionName="addTransaction"
          transitionEnterTimeout={500}
          transitionLeaveTimeout={500}
        >
          {this.state.showIncentiveFields && (
            <div>
              <div style={{ textAlign: 'center', fontSize: '14px' }}>
                Instead of lowering your rent, it is best practice to keep your rent at market rates
                and provide a rental incentive instead. Selecting &rsquo;Monthly (for the Entire
                Lease)&rsquo; will apply the incentive monthly to every upcoming payment (for whole
                months only) up until lease expiration.
              </div>
              <div style={{ textAlign: 'center' }}>
                <FieldArray
                  name="rental_incentives"
                  component={RentalIncentiveFields}
                  filteredConcessionIds={selectedConcessionIds}
                  concessions={this.props.currentConcessionsOfUnit}
                  onConcessionSelect={this.onConcessionSelect}
                  rerenderOnEveryChange
                />
              </div>
            </div>
          )}
        </CSSTransitionGroup>
      </div>
    );
  };
};

const mapDispatchToProps = (dispatch) => ({
  changeFieldValue: (field, value) => {
    dispatch(change('add_lead_form', field, value));
  },
  getProperty: (propertyId) => {
    dispatch(getProperty(propertyId));
  },
  getUnitTypes: (propertyId) => {
    dispatch(getUnitTypes(propertyId, { available_wait_units: true }));
  },
  getLandlordTrafficSources: (propertyId) => {
    dispatch(getLandlordTrafficSources(propertyId));
  },
});

export default connect((store) => {
  const selector = formValueSelector('add_lead_form');
  return {
    leadCard: selector(store, 'lead_card'),
    yardiEnabled: store.userTypes.yardiEnabled,
    paymentProvider: store.userTypes.paymentProvider,
    currentConcessionsOfUnit: store.landlordProperties.currentConcessionsOfUnit,
    unitTypes: store.landlordProperties.unitTypes,
    abilityToDelayUnitAssignment:
      store.companyDetails.companyPreferences.ability_to_delay_unit_assignment,
    delayUnitAssignment: selector(store, 'delay_unit_assignment'),
    selectedConcessions: selector(store, 'rental_incentives'),
  };
}, mapDispatchToProps)(LeaseTermsForm);
